import React, {useState, forwardRef} from 'react';
import cx from 'classnames';
import _ from 'lodash';

import Link from '../../components/Link';
import {CommentEditor} from '../../components/CommentEditor';
import Comment from '../../components/Comment';
import {ShortRightArrowIcon} from '../../components/svg';
import {useAppContext, useAnalytics} from '../../hooks';

const LessonCommentSection = forwardRef(({
  questionID, numAnswers = 1, className, commentData, header = 'Now you try', open,
}, ref) => {
  const [submitted, setSubmitted] = useState(false);
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();

  const clickSeeMore = () => {
    tracker.lessonClickSeeMoreAnswers();
  };

  const onSubmit = () => {
    setSubmitted(true);
    tracker.lessonSubmitComment();
  };

  const onOpen = () => {
    tracker.lessonStartComment();
  };

  const comments = commentData.root;

  return (
    <div className={cx('text-base flex flex-col', className)} ref={ref}>
      <div className="text-xl sm:text-2xl font-bold">{header}</div>
      <div className="flex flex-col md:rounded-lg md:shadow-sm bg-gray-50 border border-grey-200 text-grey-900 p-4 my-4">
        <div className="font-semibold mb-2">💬 Community guidelines</div>
        <ul className="ml-6 list-disc text-gray-600">
          <li>
            <span className="font-semibold">Stay on topic.</span>
            {' '}
            Use this section for submitting solutions and providing feedback to others.
          </li>
          <li>
            <span className="font-semibold">Be inclusive.</span>
            {' '}
            Exponent is a diverse community. Please respect others&apos; opinions and beliefs.
          </li>
        </ul>
      </div>
      <div className="mb-4">
        How would you answer?
        Submit your response below and get feedback from the community.
      </div>
      {currentUser && !submitted && (
        <CommentEditor
          questionID={questionID}
          onSubmit={onSubmit}
          onOpen={onOpen}
          isOpen={open}
          className="mb-6"
        />
      )}
      {submitted && (
      <div className="mb-6 p-4 text-base">
        Thank you for submitting your answer! Compare your answer with community-generated
        answers to this question in our
        {' '}
        <Link href={`/questions/${questionID}`} className="text-indigo-700">
          interview question database.
        </Link>
      </div>
      )}
      {(comments.length > 0) && (
        <div id="answers">
          {comments.slice(0, numAnswers).map((c) => (
            <Comment
              questionID={questionID}
              data={c}
              replies={commentData[c.id]}
              onSubmit={onSubmit}
              submitted={submitted}
              key={c.id}
            />
          ))}
        </div>
      )}
      <Link
        href={`/questions/${questionID}`}
        onClick={clickSeeMore}
        className="py-4 font-semibold text-base text-indigo-700 self-end"
      >
        See community answers
        <ShortRightArrowIcon className="ml-2" />
      </Link>
    </div>
  );
});

export default LessonCommentSection;
